import React, { createRef, useEffect, useState } from "react";
import { VisuallyHidden } from "react-aria";
import { t } from "ttag";

import { VideoChooserBlockWithOptions as Value } from "@reactivated";

import { VideoChooserBlock } from "@thelabnyc/thelabui/src/components/VideoChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable } from "../Clickables";
import { LogoLoader } from "../LogoLoader";
import { Svg } from "../Svg";
import { useDetectAudio } from "./utils";

import styles from "./index.module.scss";

export const VideoPlayerControls = ({
    value,
    wrapperAttrs,
    attrs,
    controlAttrs,
    playbackBehavior = "press_to_play",
}: {
    value: NonNullable<Value["video"]>;
    wrapperAttrs?: React.HTMLAttributes<HTMLDivElement>;
    attrs?: React.HTMLAttributes<HTMLVideoElement>;
    controlAttrs?: React.HTMLAttributes<HTMLDivElement>;
    playbackBehavior: Value["playback_behavior"];
}) => {
    const autoplay = playbackBehavior === "autoplay";
    const startMuted = playbackBehavior === "autoplay";
    const useCenteredPlayButton = playbackBehavior === "press_to_play";
    const videoRef = createRef<HTMLVideoElement>();

    // Tracks whether or not the video loadeddata event has been called yet.
    const [isPlayable, setIsPlayable] = useState(false);

    // Tracks whether or not the video _should_ be playing.
    const [isPlaying, setIsPlaying] = useState(autoplay);

    const showCenteredPlayButton = useCenteredPlayButton && !isPlaying;

    // Tracks whether or not the video is currently loading.
    const [isLoading, setIsLoading] = useState(true);

    // Tracks whether or not the video is currently visible in the viewport.
    const [isVisible, setIsVisible] = useState(false);

    // Tracks whether or not the video should have sources yet.
    const [loadVideoSources, setLoadVideoSources] = useState(false);

    // Tracks whether the video is currently muted
    const [isMuted, setIsMuted] = useState(startMuted);

    const toggleIsPlaying = () => {
        setIsPlaying((isPlaying) => !isPlaying);
    };

    /**
     * When the video first becomes visible, load the sources.
     */
    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        if (isVisible) {
            setLoadVideoSources(true);
        }
    }, [videoRef.current, isVisible]);

    /**
     * When the video first becomes visible, load the sources.
     */
    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        if (isVisible) {
            setLoadVideoSources(true);
        }
        /**
         * Manually set visibility of subtitles. If a track matches the active
         * locale it should be on by default, unless it's English. All other
         * tracks should be hidden.
         *
         * This overrides default browser behavior which seems to favor turning
         * on a subtitle by default if it's there as well as using the browser's/
         * system's language setting to determine the track.
         */
        [...videoRef.current.textTracks].forEach((track) => {
            const trackLanguage = track.language.substring(0, 2);
            const activeLanguage = "en";
            if (trackLanguage === activeLanguage) {
                track.mode = "showing";
            } else {
                track.mode = "hidden";
            }
        });
    }, [videoRef.current, isVisible]);

    /**
     * Autoplay the video as soon as it's visible and has finished loading (if autoplay is enabled).
     */
    useEffect(() => {
        if (!videoRef.current) return;

        if (autoplay && !isLoading && isVisible && isPlayable) {
            setIsPlaying(true);
        } else if (autoplay) {
            setIsPlaying(false);
        }
    }, [videoRef.current, isLoading, isVisible, isPlayable, autoplay]);

    /**
     * When the isPlaying state changes, update the DOM state to match.
     */
    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        if (isPlayable && isPlaying) {
            void videoRef.current.play();
        } else {
            void videoRef.current.pause();
        }
    }, [videoRef.current, isPlaying]);

    /**
     * Wait until after everything else is done loading to add sources to the
     * video element for page load/performance reasons.
     */
    useEffect(() => {
        const contentLoaded = () => {
            if (document.readyState === "complete") {
                setLoadVideoSources(true);
            }
        };
        document.addEventListener("readystatechange", contentLoaded);
        return () => {
            document.removeEventListener("readystatechange", contentLoaded);
        };
    }, []);

    /**
     * When the video element finishes loading metadata, set the playable boolean.
     */
    useEffect(() => {
        const setPlayable = () => {
            setIsPlayable(true);
        };
        const videoElem = videoRef.current;
        if (videoElem) {
            videoElem.addEventListener("loadeddata", setPlayable);
        }
        return () => {
            if (videoElem) {
                videoElem.removeEventListener("loadeddata", setPlayable);
            }
        };
    }, [videoRef.current]);

    /**
     * Track if the video element is visible or not, and update the corresponding state bool.
     */
    useEffect(() => {
        const callback: IntersectionObserverCallback = (entries) => {
            entries.forEach((entry) => {
                if (!(entry.target instanceof HTMLVideoElement)) return;
                setIsVisible(entry.isIntersecting);
            });
        };
        const observer = new IntersectionObserver(callback, {
            threshold: 0.1,
        });
        const videoElem = videoRef.current;
        if (videoElem) {
            observer.observe(videoElem);
        }
        return () => {
            if (videoElem) {
                observer.unobserve(videoElem);
            }
        };
    }, [videoRef.current]);

    const modifiedValue = {
        ...value,
        sources: loadVideoSources ? value.sources : [],
    };
    const hasAudio = useDetectAudio(videoRef, modifiedValue.sources);
    return (
        <div
            {...wrapperAttrs}
            className={concatClassNames([
                styles.root,
                wrapperAttrs?.className || "",
            ])}
        >
            {isLoading && (
                <>
                    <div className={styles.loaderOverlay} aria-hidden="true" />
                    <LogoLoader className={styles.loaderContainer} />
                </>
            )}
            <VideoChooserBlock
                ref={videoRef}
                attrs={{
                    loop: autoplay,
                    controls: false,
                    preload: "auto",
                    autoPlay: autoplay,
                    playsInline: true,
                    muted: isMuted,
                    onLoadStart: () => {
                        setIsLoading(true);
                    },
                    onWaiting: () => {
                        setIsLoading(false);
                    },
                    onPlaying: () => {
                        setIsLoading(false);
                    },
                    onCanPlayThrough: () => {
                        setIsLoading(false);
                    },
                    onPause: (e) => {
                        setIsPlaying(!e.currentTarget.paused);
                    },
                    onPlay: (e) => {
                        setIsPlaying(!e.currentTarget.paused);
                    },
                    onVolumeChange: (e) => {
                        setIsMuted(e.currentTarget.muted);
                    },
                    disablePictureInPicture: true,
                    ...attrs,
                }}
                value={modifiedValue}
            />
            {showCenteredPlayButton ? (
                <Clickable
                    className={styles.centeredPlayButton}
                    disabled={isLoading}
                    onPress={toggleIsPlaying}
                >
                    <VisuallyHidden>{t`Play Video`}</VisuallyHidden>
                    <div className={styles.centeredPlayButtonIcon}>
                        <Svg name="video/play" aria-hidden="true" />
                    </div>
                </Clickable>
            ) : (
                <div
                    className={concatClassNames([
                        styles.playControl,
                        controlAttrs?.className || "",
                    ])}
                >
                    {hasAudio && (
                        <Clickable
                            disabled={isLoading}
                            onPress={() => setIsMuted(!isMuted)}
                            className={styles.clickable}
                        >
                            <VisuallyHidden>
                                {isMuted ? "Unmute" : "Mute"} Video
                            </VisuallyHidden>
                            <Svg
                                name={isMuted ? "audio-off" : "audio-on"}
                                aria-hidden="true"
                            />
                        </Clickable>
                    )}
                    <Clickable
                        disabled={isLoading}
                        onPress={toggleIsPlaying}
                        className={styles.clickable}
                    >
                        <VisuallyHidden>
                            {isPlaying ? t`Pause Video` : t`Play Video`}
                        </VisuallyHidden>
                        <Svg
                            name={isPlaying ? "video/pause" : "video/play"}
                            aria-hidden="true"
                        />
                    </Clickable>
                </div>
            )}
        </div>
    );
};
